import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PaymentFormStripe } from '../components/PaymentFormStripe';
import { SubscriptionManagement } from '../components/SubscriptionManagement';
import { StripeService } from '../services/stripeService';
import { supabase } from '../services/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const SUBSCRIPTION_CHECK_TIMEOUT_MS = 12000;

export default function Subscription() {
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const checkSubscription = useCallback(async () => {
    setLoadError(null);
    let done = false;
    const timeoutId = setTimeout(() => {
      if (done) return;
      done = true;
      console.warn('Subscription check timed out');
      if (mountedRef.current) {
        setLoading(false);
        setLoadError('Loading subscription took too long. Please refresh or try again.');
        setHasActiveSubscription(false);
      }
    }, SUBSCRIPTION_CHECK_TIMEOUT_MS);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (done) return;
      if (user?.app_metadata?.is_free === true) {
        setIsFreeUser(true);
        setHasActiveSubscription(true);
        return;
      }

      setIsFreeUser(false);
      const stripeService = new StripeService();
      const hasActive = await stripeService.hasActiveSubscription();
      if (done) return;
      setHasActiveSubscription(hasActive);
    } catch (err) {
      if (done) return;
      console.error('Error checking subscription:', err);
      setHasActiveSubscription(false);
      setLoadError(err instanceof Error ? err.message : 'Could not load subscription.');
    } finally {
      done = true;
      clearTimeout(timeoutId);
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    checkSubscription();
    return () => {
      mountedRef.current = false;
    };
  }, [checkSubscription]);

  const handleSubscriptionSuccess = () => {
    // Refresh subscription status
    checkSubscription();
  };

  if (loading && !loadError) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <p className="text-muted-foreground">Loading subscription information...</p>
          </div>
        </div>
      </div>
    );
  }

  const errorBanner = loadError ? (
    <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
      <p className="font-medium">Subscription status could not be loaded</p>
      <p className="text-sm mt-1">{loadError}</p>
      <button
        type="button"
        onClick={() => {
          setLoadError(null);
          setLoading(true);
          checkSubscription();
        }}
        className="mt-3 text-sm font-medium text-amber-800 underline hover:no-underline"
      >
        Try again
      </button>
    </div>
  ) : null;

  if (isFreeUser) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Subscription</h1>
          <p className="text-muted-foreground">
            Manage your subscription and billing information
          </p>
        </div>
        {errorBanner}
        <div className="rounded-lg border border-green-300 bg-green-50 p-5 text-green-900">
          <p className="font-medium">This account has free access.</p>
          <p className="text-sm mt-1">
            No subscription or payment method is required while free access is enabled.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Subscription</h1>
        <p className="text-muted-foreground">
          Manage your subscription and billing information
        </p>
      </div>
      {errorBanner}
      <Tabs defaultValue={hasActiveSubscription ? 'manage' : 'plans'} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="manage">Manage</TabsTrigger>
        </TabsList>
        
        <TabsContent value="plans" className="mt-6">
          <PaymentFormStripe onSuccess={handleSubscriptionSuccess} />
        </TabsContent>
        
        <TabsContent value="manage" className="mt-6">
          <SubscriptionManagement />
        </TabsContent>
      </Tabs>
    </div>
  );
}






